/*
 * The suggestion list of the combobox. It never takes focus — the textarea keeps it and points at
 * the active option through `aria-activedescendant` — which is why the mouse path prevents the
 * mousedown default. A group item wears the group chip (spec 06 §4) so it is never mistaken for a
 * person.
 *
 * Items may come in runs under a heading (`AutocompleteItem.group`): each run is a `role="group"`
 * named by its heading, as a grouped listbox is built, so a screen reader hears which group an
 * option is in and the heading is never an option. A list longer than the box has room for
 * scrolls, and keeps the active option in view by scrolling itself alone — never the page or a pane
 * around it. An item that cannot be chosen now is drawn dimmed and marked `aria-disabled`, with its
 * detail saying why.
 *
 * A source may ask for more than options (`AutocompleteSource.footerKey`, `wide`): a command palette
 * is wider than the box and ends in a line with the keys it answers and its own sentence. That line
 * stands outside the listbox — a listbox holds options and groups only — and describes it.
 */

import { useEffect, useRef } from "react"
import { cx } from "../../../lib/cx.ts"
import { t, translate } from "../../i18n/index.ts"
import { Icon } from "../../shell/index.ts"
import type { AutocompleteItem } from "../types.ts"
import type { AutocompleteState } from "./useAutocomplete.ts"

function labelOf(item: AutocompleteItem): string {
	return item.labelKey !== undefined ? translate(item.labelKey) : (item.label ?? item.id)
}

export function AutocompleteList({
	state,
	offset,
}: {
	state: AutocompleteState
	/**
	 * Where the list's start edge stands, in pixels from the box's: a formatted surface puts it
	 * under the caret. Absent, the list spans the box, which is where a textarea's caret cannot be
	 * measured anyway.
	 */
	offset?: number | undefined
}) {
	const list = useRef<HTMLDivElement>(null)
	const activeId = state.open ? state.optionId(state.activeIndex) : undefined
	// The active option stays in view when the arrows take it past the list's edge.
	useEffect(() => {
		const box = list.current
		if (box === null || activeId === undefined) return
		const option = box.querySelector<HTMLElement>(`#${CSS.escape(activeId)}`)
		if (option !== null) keepInView(box, option)
	}, [activeId, state.items])

	if (!state.open) return null
	const option = (item: AutocompleteItem, index: number) => {
		const group = item.kind === "group"
		return (
			<div
				key={item.id}
				id={state.optionId(index)}
				role="option"
				// The textbox keeps focus and names the active option; an option is reachable by
				// script only, never by Tab.
				tabIndex={-1}
				aria-selected={index === state.activeIndex}
				{...(item.disabled === true && { "aria-disabled": true })}
				className={cx(
					"tb-composer__option",
					index === state.activeIndex && "tb-composer__option--active",
					group && "tb-composer__option--group",
					item.disabled === true && "tb-composer__option--disabled",
				)}
				onMouseDown={(e) => {
					e.preventDefault()
					state.choose(index)
				}}
				onMouseEnter={() => {
					if (item.disabled !== true) state.setActive(index)
				}}
			>
				{group ? (
					<span className="tb-tag tb-tag--group tb-composer__chip">
						<Icon name="people" size={16} />
						{labelOf(item)}
					</span>
				) : (
					<span className="tb-composer__option-label">{labelOf(item)}</span>
				)}
				{item.badge === undefined ? null : (
					<span className={item.badgeTone === "warning" ? "tb-pill tb-pill--warning" : "tb-pill"}>
						{item.badge}
					</span>
				)}
				<span className="tb-composer__option-detail">
					{group ? t("composer.suggestion.group", { count: item.memberCount ?? 0 }) : item.detail}
				</span>
			</div>
		)
	}
	const footerKey = state.source?.footerKey
	const wide = state.source?.wide === true
	// A list that is options alone is the floating box itself, as it always was.
	const bare = footerKey === undefined && !wide
	const place =
		offset === undefined ? undefined : { insetInlineStart: offset, insetInlineEnd: "auto" }
	const footerId = `${state.listboxId}-footer`
	const listbox = (
		<div
			ref={list}
			id={state.listboxId}
			role="listbox"
			aria-label={t("composer.suggestions")}
			{...(footerKey !== undefined && { "aria-describedby": footerId })}
			className={bare ? "tb-elevated tb-surface tb-composer__listbox" : "tb-composer__options"}
			style={bare ? place : undefined}
		>
			{runsOf(state.items).map((run) => {
				const options = run.items.map(({ item, index }) => option(item, index))
				if (run.group === undefined) return options
				const headingId = `${state.listboxId}-g${String(run.first)}`
				return (
					<div key={headingId} role="group" aria-labelledby={headingId}>
						<div id={headingId} role="presentation" className="tb-composer__heading">
							{run.group}
						</div>
						{options}
					</div>
				)
			})}
		</div>
	)
	if (bare) return listbox
	return (
		<div
			className={cx(
				"tb-elevated tb-surface tb-composer__listbox tb-composer__popup",
				wide && "tb-composer__popup--wide",
			)}
			style={place}
		>
			{listbox}
			{footerKey === undefined ? null : (
				<div id={footerId} className="tb-composer__listfoot">
					<span className="tb-composer__listkeys">{t("composer.suggestions.keys")}</span>
					<span>{translate(footerKey)}</span>
				</div>
			)}
		</div>
	)
}

/** A run of items under one heading, or of items with none; each item keeps its index in the list. */
interface Run {
	group: string | undefined
	first: number
	items: { item: AutocompleteItem; index: number }[]
}

function runsOf(items: readonly AutocompleteItem[]): Run[] {
	const runs: Run[] = []
	items.forEach((item, index) => {
		const last = runs.at(-1)
		if (last !== undefined && last.group === item.group) last.items.push({ item, index })
		else runs.push({ group: item.group, first: index, items: [{ item, index }] })
	})
	return runs
}

/** Scrolls the list, and nothing around it, so that the option stands wholly inside it. */
function keepInView(box: HTMLElement, option: HTMLElement): void {
	const top = option.offsetTop
	const bottom = top + option.offsetHeight
	if (top < box.scrollTop) box.scrollTop = top
	else if (bottom > box.scrollTop + box.clientHeight) box.scrollTop = bottom - box.clientHeight
}
